import { PaginationState, SortingState, Updater } from "@tanstack/react-table";
import { useMemo } from "react";

import { LeadV2, LeadV2Status } from "../../api/generated";
import { useIsMobile } from "../../hooks";
import { TableDesktop, TableMobile } from "../../Table";
import { useDebounceFn } from "../../utils/debounce";
import { useLeadsContext } from "../providers/LeadsProvider";
import {
  LeadsPageAnalytics,
  ReturnedTranslatedContentLeadPageHook,
} from "../types";
import { leadColumns } from "./Columns";
import { MobileCardBody } from "./MobileCard";

type LeadTableProps = {
  isBackoffice?: boolean;
  showPagination?: boolean;
  translations: ReturnedTranslatedContentLeadPageHook;
  analytics?: LeadsPageAnalytics;
};

export const LeadTable = ({
  isBackoffice,
  translations,
  showPagination = true,
  analytics,
}: LeadTableProps) => {
  const { translatedContent, countryCodeMapping, leadStatusMapping, language } =
    translations;
  const {
    state: { page, pageSize, sortBy, sortOrder },
    resources: { table },
    actions: { setSort, setPage },
    runResource,
  } = useLeadsContext();
  const isMobile = useIsMobile();

  const { error } = table;
  const { leads = [], redactedFields, totalCount } = table.data ?? {};
  const canViewPII = redactedFields?.includes("clientId");
  const isLoading = table.status === "loading";
  const pageCount = Math.ceil((totalCount ?? 0) / (pageSize ?? 10));

  const translatedLeads = useMemo(() => {
    return (leads as LeadV2[]).map((lead: LeadV2) => ({
      ...lead,
      tCountry: countryCodeMapping.get(lead.countryCode) ?? "-",
      tStatus:
        leadStatusMapping.get(lead.status) ??
        (leadStatusMapping.get(LeadV2Status.Unknown) as string),
      tDate: lead.createdAt,
    }));
  }, [countryCodeMapping, leadStatusMapping, leads]);

  const columnVisibility = useMemo(() => {
    return redactedFields?.length
      ? redactedFields.reduce<Record<string, boolean>>(
          (acc: Record<string, boolean>, field: string) => {
            acc[field] = false;
            return acc;
          },
          {},
        )
      : { clientId: false };
  }, [redactedFields]);

  if (canViewPII) columnVisibility.clientId = false;

  const sorting = useMemo(
    () =>
      sortBy != null && sortOrder != null
        ? [{ desc: sortOrder === "desc", id: sortBy }]
        : [],
    [sortOrder, sortBy],
  );

  const tableConfig = useMemo(
    () => ({
      translatedContent,
      language,
      columnVisibility,
    }),
    [translatedContent, language, columnVisibility],
  );

  const [trackTooltipEvent] = useDebounceFn(
    (status: LeadV2Status, isMobile: boolean) => {
      if (analytics?.trackStatusTooltipEvent) {
        return analytics.trackStatusTooltipEvent(status, isMobile);
      }
    },
    2000,
  );

  const columns = leadColumns({
    ...tableConfig,
    columnLabels: translatedContent.columnLabels,
    redactedFields,
    isBackoffice,
    trackTooltipEvent,
  });

  const handleSortingUpdate = (updater: Updater<SortingState>) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;
    const newSort = nextSorting[0];
    if (newSort.id !== sortBy) {
      setSort?.(newSort.id, "desc");
    } else {
      setSort?.(nextSorting[0].id, nextSorting[0].desc ? "desc" : "asc");
    }
  };

  const handlePaginationChange = (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => {
    const newPagination =
      typeof updater === "function"
        ? updater({
            pageIndex: page,
            pageSize,
          })
        : updater;

    setPage(newPagination.pageIndex);
  };

  if (isMobile) {
    return (
      <div data-testid="mobile-table-content">
        <TableMobile
          data={translatedLeads || []}
          onRetry={() => runResource("table")}
          columns={columns}
          isError={Boolean(error)}
          showPagination={showPagination}
          translatedContent={translatedContent}
          cardHeader={() => null}
          cardBody={MobileCardBody({
            columnLabels: tableConfig.translatedContent.columnLabels,
            canViewPII,
            language: tableConfig.language,
            onHover: trackTooltipEvent,
          })}
          expandable={false}
          filterFns={{} as any}
          isLoading={isLoading}
          enableServerSidePagination={true}
          pageCount={pageCount}
          totalCount={totalCount}
          state={{ pagination: { pageIndex: page, pageSize } }}
          onPaginationChange={handlePaginationChange}
        />
      </div>
    );
  }
  return (
    <TableDesktop
      data={translatedLeads || []}
      onRetry={() => runResource("table")}
      isError={Boolean(error)}
      columns={columns}
      showPagination={showPagination}
      translatedContent={translatedContent}
      enableSorting
      enabledServerSideSorting
      sorting={sorting}
      onSortingChange={handleSortingUpdate}
      pageSize={pageSize}
      isLoading={isLoading}
      filterFns={{} as any} // No client-side filter functions
      // Server-side pagination props
      enableServerSidePagination={true}
      pageCount={pageCount}
      totalCount={totalCount}
      state={{ pagination: { pageIndex: page, pageSize } }}
      onPaginationChange={handlePaginationChange}
    />
  );
};
